<template>
  <div class="home-page">
    <section class="home-hero">
      <div class="home-hero__fallback" role="img" aria-label="玉雕作品"></div>

      <div class="home-hero__shade"></div>
      <div class="site-shell home-hero__content">
        <span class="collection-seal">馆藏档案</span>
        <p class="eyebrow">中国玉雕大师与作品收藏档案平台</p>
        <h1>中国玉雕大师收藏网</h1>
        <p class="home-hero__lead">收录大师档案、代表作品与收藏证书，建立可查询、可追溯的当代玉雕馆藏记录。</p>
        <div class="home-hero__actions">
          <router-link to="/gallery" class="jade-button jade-button--light">浏览馆藏 <i class="el-icon-arrow-right"></i></router-link>
          <router-link to="/masters" class="jade-button jade-button--ghost">大师名录</router-link>
        </div>
      </div>
    </section>

    <section class="verification-band">
      <div class="site-shell verification-band__inner">
        <div class="verification-band__copy">
          <div>
            <p class="eyebrow">馆藏证书核验</p>
            <h2>查询收藏证书档案</h2>
            <p>输入证书编号与验证码，核验馆藏作品记录。</p>
          </div>
        </div>
        <el-form ref="queryForm" :model="queryForm" :rules="queryRules" class="quick-query">
          <el-form-item prop="certNo">
            <el-input v-model="queryForm.certNo" placeholder="证书编号" prefix-icon="el-icon-document"></el-input>
          </el-form-item>
          <el-form-item prop="captcha">
            <div class="captcha-field">
              <el-input v-model="queryForm.captcha" placeholder="验证码" prefix-icon="el-icon-key" @keyup.enter.native="submitQuery"></el-input>
              <button type="button" class="captcha-code" title="点击刷新验证码" @click="refreshCaptcha">{{ captchaText }}</button>
            </div>
          </el-form-item>
          <el-form-item class="quick-query__actions">
            <el-button type="primary" @click="submitQuery">立即查询</el-button>
            <el-button title="重置" icon="el-icon-refresh" @click="resetQuery"></el-button>
          </el-form-item>
        </el-form>
      </div>
    </section>

    <section class="content-section content-section--works">
      <div class="site-shell">
        <header class="editorial-heading">
          <div>
            <p class="eyebrow">馆藏作品</p>
            <h2>新入藏玉作</h2>
          </div>
          <p>按馆藏编号浏览作品材质、构思与雕刻细节。</p>
          <router-link to="/gallery" class="text-link">查看全部 <i class="el-icon-arrow-right"></i></router-link>
        </header>

        <div v-if="collectionList.length" class="work-preview-grid">
          <article v-for="item in collectionList.slice(0, 8)" :key="item.id" class="work-preview" @click="$router.push('/gallery')">
            <div class="work-preview__image">
              <img v-if="item.artworkImage" :src="getImageUrl(item.artworkImage)" :alt="item.name" @error="handleImageError">
              <div v-else class="image-placeholder"><i class="el-icon-picture-outline"></i></div>
              <span v-if="item.isRare" class="work-preview__badge">珍藏</span>
            </div>
            <div class="work-preview__info">
              <h3>{{ item.name }}</h3>
              <p>{{ item.description || '玉雕作品' }}</p>
            </div>
          </article>
        </div>
        <el-empty v-else description="暂无作品数据"></el-empty>
      </div>
    </section>

    <section class="content-section content-section--artisans">
      <div class="site-shell">
        <header class="editorial-heading editorial-heading--inverse">
          <div>
            <p class="eyebrow">大师档案</p>
            <h2>名家与技艺</h2>
          </div>
          <p>收录当代玉雕大师的专业履历、代表作品与技艺方向。</p>
          <router-link to="/masters" class="text-link">大师名录 <i class="el-icon-arrow-right"></i></router-link>
        </header>

        <div class="artisan-preview-grid">
          <article v-for="item in masterList.slice(0, 6)" :key="item.id" class="artisan-preview" @click="goToMasterDetail(item.id)">
            <div class="artisan-preview__portrait">
              <img v-if="item.avatar" :src="getFullImageUrl(item.avatar)" :alt="item.masterName" @error="handleImageError">
              <div v-else class="image-placeholder"><i class="el-icon-user"></i></div>
            </div>
            <div class="artisan-preview__info">
              <h3>{{ item.masterName }}</h3>
              <p>{{ item.craft || item.title || '玉雕技艺' }}</p>
              <span>查看档案 <i class="el-icon-arrow-right"></i></span>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="content-section content-section--news">
      <div class="site-shell">
        <header class="editorial-heading">
          <div>
            <p class="eyebrow">馆藏资讯</p>
            <h2>收藏与行业观察</h2>
          </div>
          <p>关注玉雕创作、文化传承与行业发展的最新动态。</p>
          <router-link to="/news" class="text-link">全部资讯 <i class="el-icon-arrow-right"></i></router-link>
        </header>

        <div class="home-news-list">
          <article v-for="item in newsList.slice(0, 5)" :key="item.id" class="home-news-item" @click="goToNewsDetail(item.id)">
            <div class="home-news-item__image">
              <img v-if="item.coverImage" :src="getFullImageUrl(item.coverImage)" :alt="item.title" @error="handleImageError">
              <div v-else class="image-placeholder"><i class="el-icon-reading"></i></div>
            </div>
            <div class="home-news-item__copy">
              <time>{{ formatDate(item.publishTime) }}</time>
              <h3>{{ item.title }}</h3>
              <p>{{ getSummary(item.content) }}</p>
            </div>
            <i class="el-icon-arrow-right home-news-item__arrow"></i>
          </article>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { getCollectionList } from '@/api/collection'
import { getMastersList } from '@/api/masters'
import { getNewsList } from '@/api/news'
import request from '@/utils/request'

export default {
  name: 'HomePage',
  data() {
    return {
      collectionList: [],
      masterList: [],
      newsList: [],
      captchaText: '',
      queryForm: { certNo: '', captcha: '' },
      queryRules: {
        certNo: [{ required: true, message: '请输入证书编号', trigger: 'blur' }],
        captcha: [{ required: true, message: '请输入验证码', trigger: 'blur' }]
      }
    }
  },
  created() {
    this.refreshCaptcha()
    this.fetchCollections()
    this.fetchMasters()
    this.fetchNews()
  },
  methods: {
    async fetchCollections() {
      try {
        const response = await getCollectionList({ pageNum: 1, pageSize: 1000 })
        if (response.code === 200) this.collectionList = this.shuffleItems(response.rows || []).slice(0, 8)
      } catch (error) {
        console.error('获取作品数据出错:', error)
      }
    },
    async fetchMasters() {
      try {
        const response = await getMastersList({ pageNum: 1, pageSize: 6 })
        if (response.code === 200) this.masterList = response.rows || []
      } catch (error) {
        console.error('获取匠人数据出错:', error)
      }
    },
    async fetchNews() {
      try {
        const response = await getNewsList({ pageNum: 1, pageSize: 1000 })
        if (response.code === 200) this.newsList = this.shuffleItems(response.rows || []).slice(0, 5)
      } catch (error) {
        console.error('获取资讯数据出错:', error)
      }
    },
    shuffleItems(items) {
      const shuffled = items.slice()
      for (let index = shuffled.length - 1; index > 0; index -= 1) {
        const target = Math.floor(Math.random() * (index + 1))
        ;[shuffled[index], shuffled[target]] = [shuffled[target], shuffled[index]]
      }
      return shuffled
    },
    refreshCaptcha() {
      this.captchaText = Math.random().toString(36).substr(2, 4).toUpperCase()
    },
    submitQuery() {
      this.$refs.queryForm.validate(valid => {
        if (!valid) return
        if (this.queryForm.captcha.toUpperCase() !== this.captchaText) {
          this.$message.error('验证码错误！')
          this.refreshCaptcha()
          return
        }
        request({ url: '/system/YddsscCertificateList/CertificateInfo/' + this.queryForm.certNo, method: 'get' })
          .then(res => {
            if (res.code === 200) this.$router.push('/certificate?id=' + this.queryForm.certNo)
            else this.$message.warning(res.msg || '证书不存在！')
          })
          .catch(() => this.$message.error('查询失败，请稍后重试'))
      })
    },
    resetQuery() {
      this.$refs.queryForm.resetFields()
      this.refreshCaptcha()
    },
    getImageUrl(path) {
      if (!path) return ''
      return path.startsWith('http://') || path.startsWith('https://') ? path : `${process.env.VUE_APP_BASE_API}${path}`
    },
    getFullImageUrl(path) {
      return this.getImageUrl(path)
    },
    handleImageError(event) {
      if (event && event.target) event.target.style.display = 'none'
    },
    getSummary(content) {
      if (!content) return '暂无内容'
      const text = content.replace(/<[^>]+>/g, '')
      return text.length > 72 ? text.substring(0, 72) + '...' : text
    },
    formatDate(dateStr) {
      return dateStr ? new Date(dateStr).toLocaleDateString() : ''
    },
    goToMasterDetail(id) {
      this.$router.push(`/masters/${id}`)
    },
    goToNewsDetail(id) {
      this.$router.push(`/news/${id}`)
    }
  }
}
</script>
